import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';

import { ToastComponent } from '../shared/toast/toast.component';
import { EsService } from '../services/es.service';
import { AuthService } from '../services/auth.service';
import { PoliticianService } from '../services/politician.service';
import { Alert } from '../shared/models/alert.model';
import { chartOptions, ee, euCountries, organs, perPage50, profilePics, sources } from '../shared/constants';
import { removeTooltipElement, cleanText, formatSource, wildcardInQuotes,
  parenthesisAreEven, doubleQuotesAreEven, saveFile, generateStatsCSV, scrollTo } from '../shared/functions';
import { exportChart, generateBarChart, generateDonutChart } from '../shared/chartFunctions';
import { BarChartParams, EsSearchParams, EsSearchStats, LabelsAndData } from '../shared/types';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  standalone: false
})
export class SearchComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  private router = inject(Router);
  private politicianService = inject(PoliticianService);
  private esService = inject(EsService);
  toast = inject(ToastComponent);


  isLoading = {
    search: null,
    stats: false,
  };
  errorMessages = {
    queryTooLong: 'Max length is 1000 characters!',
    wildcardInQuotes: 'Wildcard operator cannot be used inside double quotes!',
    parenthesisAreEven: 'Missing closing parenthesis!',
    doubleQuotesAreEven: 'Missing closing double quote!',
  };
  profilePics = profilePics;
  euCountries = euCountries;
  organs = organs;
  perPage = perPage50;
  formatSource = formatSource;
  exportChart = exportChart;
  isSearching = false;
  searchHasError = false;
  // Search
  keyword = '';
  person = '';
  noperson = '';
  source = '';
  sources = [];
  sourcesFixed = [...sources]; // It needs to be immutable copy
  commission = '';
  commissions = [];
  days = '90';
  lang = '';
  party = '';
  parties = [];
  partiesEU = [];
  organ = '';
  country = '';
  // Arrays
  esSearchParams: EsSearchParams;
  results = [];
  count = 0;
  page = 0;
  // Stats
  chartParams: BarChartParams = {
    label: 'posts',
  };
  statsResults: EsSearchStats = {};
  barChartTopUsersInstitutions;
  barChartTopUsersMedia;
  barChartTopUsersFollowers;
  barChartTopParties;
  barChartTopCommissions;
  barChartTopWords;
  donutChartSentiment;
  skip = {
    usersInstitutions: 0, usersMedia: 0, usersFollowers: 0, commissions: 0
  };
  currentContent = 'search';
  menuItems = [
    { name: 'Search', link: 'search', icon: 'search' },
    // { name: 'Statistics', link: 'stats', icon: 'bar-chart' },
    { name: '', link: '', icon: '' }, // Workaround to create a separator
    { name: 'All alerts', link: 'alerts', icon: 'bell' },
    { name: 'My alerts', link: 'my-alerts', icon: 'user' },
  ];

  ngOnInit(): void {
    ee.on('changeContent', (data: string) => this.changeContentFn(data));
    ee.on('runAlertQuery', (alert: Alert) => this.runAlertQuery(alert));
    ee.on('getSearch', (page?: number) => this.search(page));
    if (!this.auth.loggedIn) {
      this.router.navigate(['/login']);
      return;
    }
    this.getCommissions();
    this.getParties();
  }

  ngOnDestroy(): void {
    ee.off('changeContent', () => {});
    ee.off('runAlertQuery', () => {});
    this.destroyCharts();
    removeTooltipElement();
  }

  changeContentFn(content: string): void {
    this.currentContent = content;
    scrollTo(0);
    switch (content) {
      case 'search':
      case 'alerts':
      case 'my-alerts':
        break;
      case 'stats':
        if (!this.results.length && this.router.url === '/') {
          this.toast.setMessage('You need to perform a search to populate this page', 'warning', 3000);
        }
        break;
    }
  }

  resetSkipObject(): void {
    Object.keys(this.skip).forEach(key => this.skip[key] = 0);
  }

  destroyCharts(): void {
    this.barChartTopUsersInstitutions?.destroy();
    this.barChartTopUsersMedia?.destroy();
    this.barChartTopUsersFollowers?.destroy();
    this.barChartTopParties?.destroy();
    this.barChartTopCommissions?.destroy();
    this.barChartTopWords?.destroy();
    this.donutChartSentiment?.destroy();
  }

  getCommissions(): void {
    this.politicianService.getCommissions()
      .subscribe({
        next: res => this.commissions = res,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getParties(): void {
    this.politicianService.getParties()
      .subscribe({
        next: res => {
          this.parties = res.filter(party => !party.parlamento_eu);
          this.partiesEU = res.filter(party => party.parlamento_eu);
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  wildcardInQuotes(): boolean {
    return wildcardInQuotes(this.keyword, this.person, this.noperson);
  }

  parenthesisAreEven(): boolean {
    return parenthesisAreEven(this.keyword, this.person, this.noperson);
  }

  doubleQuotesAreEven(): boolean {
    return doubleQuotesAreEven(this.keyword, this.person, this.noperson);
  }

  isSearchDisabled() {
    return this.isLoading.search ||
      (this.keyword.length < 2 && this.person.length < 2) ||
      this.wildcardInQuotes() || !this.doubleQuotesAreEven() || !this.parenthesisAreEven();
  }

  search(page?: number): void {
    this.isLoading.search = true;
    this.isSearching = true;
    this.searchHasError = false;
    this.page = page || 0;
    this.keyword = cleanText(this.keyword.trim());
    this.person = cleanText(this.person.trim());
    this.noperson = cleanText(this.noperson.trim());
    const euPartySuffix = this.partiesEU.map(p => p._id).includes(this.party) ? 'eu' : '';
    this.esSearchParams = {
      keyword: this.keyword,
      person: this.person,
      noperson: this.noperson,
      source: this.source,
      lang: this.lang,
      party: this.party,
      days: this.days,
      organ: this.organ,
      commission: this.commission,
      country: this.country,
      euPartySuffix,
      page: this.page,
      perPage: this.perPage,
    };
    this.esService.search(this.esSearchParams)
      .pipe(finalize(() => this.isLoading.search = false))
      .subscribe({
        next: res => {
          this.results = res.results;
          this.count = res.count;
          if (!page) {
            this.resetSkipObject();
            // this.searchStats();
          }
        },
        error: err => {
          this.toast.setMessage(err.error.message, 'danger');
          this.searchHasError = true;
          this.results = [];
          this.count = 0;
        },
      });
  }

  searchStats(): void {
    this.isLoading.stats = true;
    this.destroyCharts();
    this.esService.searchStats(this.esSearchParams)
      .pipe(finalize(() => this.isLoading.stats = false))
      .subscribe({
        next: res => {
          this.statsResults = res;
          const { usersInstitutions, usersMedia, usersFollowers, parties, commissions, topWords, sentiment } = res;
          const usersInstitutionsSlice = this.sliceLabelsAndData(usersInstitutions, 0);
          const usersMediaSlice = this.sliceLabelsAndData(usersMedia, 0);
          const usersFollowersSlice = this.sliceLabelsAndData(usersFollowers, 0);
          const commissionsSlice = this.sliceLabelsAndData(commissions, 0);
          const chartOptionsUsernameClick: BarChartParams = {
            ...this.chartParams,
            cursorPointer: true,
            plugins: [chartOptions.plugins.openTwitterOnClick]
          };
          this.barChartTopUsersInstitutions = generateBarChart(usersInstitutionsSlice, '1', chartOptionsUsernameClick);
          this.barChartTopUsersMedia = generateBarChart(usersMediaSlice, '2', chartOptionsUsernameClick);
          this.barChartTopParties = generateBarChart(parties, '3', { ...this.chartParams, isPartiesChart: true });
          this.barChartTopCommissions = generateBarChart(commissionsSlice, '4', this.chartParams);
          this.barChartTopUsersFollowers = generateBarChart(usersFollowersSlice, '5', { ...chartOptionsUsernameClick, label: 'followers' });
          this.barChartTopWords = generateBarChart(topWords, '6', { label: 'occurrences' });
          this.donutChartSentiment = generateDonutChart(sentiment, '1', '#fff');
        },
        error: err => this.toast.setMessage(err.error.message, 'danger')
      });
  }

  sliceLabelsAndData(stats: LabelsAndData, start: number, limit = 10) {
    return {
      labels: stats.labels.slice(start, limit + start),
      data: stats.data.slice(start, limit + start)
    };
  }

  getStatsByModelSkip(nextOrPrev: 'next' | 'prev', model: string): void {
    const criteria = {
      usersInstitutions: {
        chart: 'barChartTopUsersInstitutions',
        num: '1',
      },
      usersMedia: {
        chart: 'barChartTopUsersMedia',
        num: '2'
      },
      commissions: {
        chart: 'barChartTopCommissions',
        num: '4'
      },
      usersFollowers: {
        chart: 'barChartTopUsersFollowers',
        num: '5',
      },
    };
    if (nextOrPrev === 'next') {
      this.skip[model] += 10;
    } else {
      this.skip[model] = this.skip[model] <= 0 ? 0 : this.skip[model] - 10;
    }
    const newStats = this.sliceLabelsAndData(this.statsResults[model], this.skip[model]);
    this[criteria[model].chart]?.destroy();
    this[criteria[model].chart] = generateBarChart(newStats, criteria[model].num, this.chartParams);
  }

  downloadCSV(what: string, header: string) {
    saveFile(generateStatsCSV(this.statsResults[what], header), `${what}.csv`);
  }

  checkSource(source: string): void {
    if (!this.sources.includes(source)) {
      this.sources.push(source);
    } else {
      const index = this.sources.findIndex(s => s === source);
      this.sources.splice(index, 1);
    }
    this.source = this.sources.length === this.sourcesFixed.length ? '' : this.sources.join(' ');
  }

  runAlertQuery(alert: Alert): void {
    removeTooltipElement();
    scrollTo(300);
    this.currentContent = 'search';
    this.keyword = alert.query.keyword;
    this.person = alert.query.person;
    this.noperson = alert.query.noperson;
    this.source = alert.query.source;
    this.sources = alert.query.source.split(' ');
    this.lang = alert.query.lang;
    this.party = alert.query.party;
    this.organ = alert.query.organ;
    this.commission = alert.query.commission;
    this.country = alert.query.country;
    this.search();
  }
}
